import React, { useEffect, useState } from "react";
import { Workflow, Plus, Pencil, Trash2, ChevronRight, GripVertical } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader, Card, Badge, Loading, Modal, Spinner, EmptyState } from "../components/ui";

const APPROVER_TYPES = ["MANAGER", "ROLE", "EMPLOYEE", "DYNAMIC"];
const ROLES = ["HR_ADMIN", "SUPERADMIN", "MANAGER"];
const OPS = ["<", "<=", ">", ">=", "==", "!="];

const blankStage = (order) => ({ order, name: "New Stage", approverType: "ROLE", role: "HR_ADMIN", level: 1, slaHours: 48, skip: null });

function stageLabel(s) {
  if (s.approverType === "MANAGER") return `Manager L${s.level || 1}`;
  if (s.approverType === "ROLE") return s.role;
  if (s.approverType === "EMPLOYEE") return "Specific Employee";
  return "Dept Head";
}

export default function Flows() {
  const [flows, setFlows] = useState(null);
  const [meta, setMeta] = useState({ types: [], approverTypes: APPROVER_TYPES, roles: ROLES });
  const [edit, setEdit] = useState(null);     // flow being edited (with stages)
  const [creating, setCreating] = useState(false);
  const [newFlow, setNewFlow] = useState({ type: "", name: "", category: "Custom" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/flows").then(({ data }) => setFlows(data));
  useEffect(() => { load(); api.get("/flows/types").then(({ data }) => setMeta(data)); }, []);

  const toggleActive = async (f) => {
    await api.put(`/flows/${f.id}`, { active: !f.active });
    load();
  };
  const del = async (f) => {
    if (!window.confirm(`Delete flow "${f.name}"? In-flight instances are unaffected.`)) return;
    await api.delete(`/flows/${f.id}`); load();
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const stages = edit.stages.map((s, i) => ({ ...s, order: i + 1 }));
      await api.put(`/flows/${edit.id}`, { name: edit.name, active: edit.active, stages });
      setEdit(null); load();
    } catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const createFlow = async () => {
    if (!newFlow.type || !newFlow.name) return alert("Pick a type and name.");
    setBusy(true);
    try {
      const { data } = await api.post("/flows", { ...newFlow, active: true, stages: [blankStage(1)] });
      setCreating(false); setNewFlow({ type: "", name: "", category: "Custom" });
      load();
      setEdit({ ...data, stages: data.stages });
    } catch (e) { alert(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const updateStage = (idx, patch) =>
    setEdit((f) => ({ ...f, stages: f.stages.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));
  const addStage = () => setEdit((f) => ({ ...f, stages: [...f.stages, blankStage(f.stages.length + 1)] }));
  const removeStage = (idx) => setEdit((f) => ({ ...f, stages: f.stages.filter((_, i) => i !== idx) }));

  if (!flows) return <Loading />;

  return (
    <div>
      <PageHeader title="Flow Manager" subtitle="Configure the stage-based approval workflows that power every module">
        <button data-testid="new-flow-btn" onClick={() => setCreating(true)} className="btn-accent flex items-center gap-1.5"><Plus size={16} /> New Workflow</button>
      </PageHeader>

      {flows.length === 0 ? <EmptyState icon={Workflow} title="No workflows configured" /> : (
        <div className="grid gap-4" data-testid="flows-list">
          {flows.map((f) => (
            <Card key={f.id} className="p-5" data-testid={`flow-${f.type}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent"><Workflow size={20} /></div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-head text-lg font-bold text-slate-900 dark:text-white">{f.name}</h3>
                      <Badge tone={f.category === "Standard" ? "info" : f.category === "Lifecycle" ? "warn" : "neutral"}>{f.category}</Badge>
                      <Badge tone={f.active ? "ok" : "bad"}>{f.active ? "Active" : "Inactive"}</Badge>
                    </div>
                    <p className="font-mono text-xs text-slate-400">{f.type} · {f.activeInstances} in-flight</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-500">
                    <input type="checkbox" data-testid={`toggle-${f.type}`} checked={f.active} onChange={() => toggleActive(f)} className="accent-accent" /> Active
                  </label>
                  <button data-testid={`edit-${f.type}`} onClick={() => setEdit({ ...f, stages: f.stages.map((s) => ({ ...s })) })} className="btn-outline px-3 py-1.5 text-sm"><Pencil size={14} className="mr-1 inline" />Edit</button>
                  <button data-testid={`delete-${f.type}`} onClick={() => del(f)} className="rounded-md p-2 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2 overflow-x-auto pb-1">
                {f.stages.map((s, i) => (
                  <React.Fragment key={i}>
                    <div className="flex items-center gap-2 whitespace-nowrap rounded-md border border-slate-200 px-3 py-1.5 text-sm dark:border-slate-700">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-500 dark:bg-slate-800">{i + 1}</span>
                      <span className="font-medium text-slate-700 dark:text-slate-200">{s.name}</span>
                      <span className="text-xs text-slate-400">· {stageLabel(s)}{s.slaHours ? ` · ${s.slaHours}h` : ""}{s.skip ? ` · skip if ${s.skip.field}${s.skip.op}${s.skip.value}` : ""}</span>
                    </div>
                    {i < f.stages.length - 1 && <ChevronRight size={16} className="shrink-0 text-slate-300" />}
                  </React.Fragment>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create flow modal */}
      <Modal open={creating} onClose={() => setCreating(false)} title="New Workflow"
        footer={<><button onClick={() => setCreating(false)} className="btn-outline">Cancel</button><button data-testid="create-flow-submit" disabled={busy} onClick={createFlow} className="btn-accent">{busy ? <Spinner className="text-white" /> : "Create & Edit"}</button></>}>
        <div className="space-y-4">
          <div><label className="label">Flow Type</label>
            <select data-testid="new-flow-type" value={newFlow.type} onChange={(e) => setNewFlow((f) => ({ ...f, type: e.target.value }))} className="input">
              <option value="">— Select —</option>
              {meta.types.map((t) => <option key={t.value} value={t.value}>{t.value} → {t.entity}</option>)}
            </select>
          </div>
          <div><label className="label">Name</label><input data-testid="new-flow-name" value={newFlow.name} onChange={(e) => setNewFlow((f) => ({ ...f, name: e.target.value }))} className="input" placeholder="e.g. High-value Expense Approval" /></div>
        </div>
      </Modal>

      {/* Edit flow / stage builder modal */}
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit ? `Edit · ${edit.name}` : ""} size="lg"
        footer={<><button onClick={() => setEdit(null)} className="btn-outline">Cancel</button><button data-testid="save-flow" disabled={busy} onClick={saveEdit} className="btn-accent">{busy ? <Spinner className="text-white" /> : "Save Workflow"}</button></>}>
        {edit && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><label className="label">Name</label><input data-testid="flow-name" value={edit.name} onChange={(e) => setEdit((f) => ({ ...f, name: e.target.value }))} className="input" /></div>
              <div className="flex items-end"><label className="flex cursor-pointer items-center gap-2 text-sm font-medium"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit((f) => ({ ...f, active: e.target.checked }))} className="accent-accent" /> Active</label></div>
            </div>

            <div className="space-y-3" data-testid="stage-editor">
              {edit.stages.map((s, idx) => (
                <div key={idx} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-testid={`stage-${idx}`}>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><GripVertical size={14} className="text-slate-300" /> Stage {idx + 1}</span>
                    {edit.stages.length > 1 && <button data-testid={`remove-stage-${idx}`} onClick={() => removeStage(idx)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>}
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="col-span-2"><label className="lbl mb-1 block">Stage Name</label><input value={s.name} onChange={(e) => updateStage(idx, { name: e.target.value })} className="input" /></div>
                    <div><label className="lbl mb-1 block">Approver</label>
                      <select value={s.approverType} onChange={(e) => updateStage(idx, { approverType: e.target.value })} className="input">
                        {meta.approverTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div><label className="lbl mb-1 block">SLA (hrs)</label><input type="number" value={s.slaHours} onChange={(e) => updateStage(idx, { slaHours: +e.target.value })} className="input" /></div>
                    {s.approverType === "MANAGER" && <div><label className="lbl mb-1 block">Level</label><input type="number" min="1" max="3" value={s.level || 1} onChange={(e) => updateStage(idx, { level: +e.target.value })} className="input" /></div>}
                    {s.approverType === "ROLE" && <div><label className="lbl mb-1 block">Role</label><select value={s.role || "HR_ADMIN"} onChange={(e) => updateStage(idx, { role: e.target.value })} className="input">{meta.roles.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>}
                  </div>
                  <div className="mt-3 rounded-md bg-slate-50 p-2.5 dark:bg-slate-800/50">
                    <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                      <input type="checkbox" data-testid={`skip-toggle-${idx}`} checked={!!s.skip} onChange={(e) => updateStage(idx, { skip: e.target.checked ? { field: "days", op: "<", value: 3 } : null })} className="accent-accent" />
                      Skip this stage if condition is met
                    </label>
                    {s.skip && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input value={s.skip.field} onChange={(e) => updateStage(idx, { skip: { ...s.skip, field: e.target.value } })} className="input w-auto py-1.5 text-sm" placeholder="field (days/amount)" />
                        <select value={s.skip.op} onChange={(e) => updateStage(idx, { skip: { ...s.skip, op: e.target.value } })} className="input w-auto py-1.5 text-sm">{OPS.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                        <input type="number" value={s.skip.value} onChange={(e) => updateStage(idx, { skip: { ...s.skip, value: +e.target.value } })} className="input w-24 py-1.5 text-sm" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <button data-testid="add-stage" onClick={addStage} className="flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-slate-200 py-2.5 text-sm font-medium text-slate-500 hover:border-accent hover:text-accent dark:border-slate-700"><Plus size={16} /> Add Stage</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
